"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { useRouter } from "next/navigation";

interface User {
    id: string;
    email: string;
    name: string;
    company_name?: string;
    role?: string;
}

interface AuthContextType {
    user: User | null;
    loading: boolean;
    login: (email: string, password: string, captchaToken?: string | null) => Promise<void>;
    loginWithGoogle: () => void;
    register: (email: string, password: string, name: string, companyName?: string) => Promise<void>;
    registerWithInvitation: (email: string, password: string, name: string, invitationToken: string) => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const router = useRouter();

    // Helper to get cookie
    const getCookie = (name: string) => {
        if (typeof document === "undefined") return null;
        const nameLenPlus = (name.length + 1);
        return document.cookie
            .split(';')
            .map(c => c.trim())
            .filter(cookie => {
                return cookie.substring(0, nameLenPlus) === `${name}=`;
            })
            .map(cookie => {
                return decodeURIComponent(cookie.substring(nameLenPlus));
            })[0] || null;
    };

    const setCookie = (name: string, value: string, days: number) => {
        if (typeof document === "undefined") return;
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        const expires = "expires=" + date.toUTCString();
        document.cookie = name + "=" + value + ";" + expires + ";path=/";
    };

    const deleteCookie = (name: string) => {
        if (typeof document === "undefined") return;
        // Standard deletion
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
        // Also try with current domain and other common variations just in case
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT; domain=${window.location.hostname};`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
    };

    const fetchUser = async (token: string) => {
        try {
            const response = await axios.get(`${API_URL}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data);
        } catch (error) {
            console.error("Failed to fetch user", error);
            // If we are on a protected route and fetch fails, we must logout
            const protectedRoutes = ['/dashboard', '/interviews'];
            if (protectedRoutes.some(route => window.location.pathname.startsWith(route))) {
                logout();
            }
        } finally {
            setLoading(false);
        }
    };
    
    // Handle Google OAuth exchange with account type info
    const exchangeGoogleCode = async (code: string, accountType: "individual" | "company" = "individual", companyName?: string) => {
        try {
            const response = await axios.post(`${API_URL}/auth/google/exchange`, {
                code,
                account_type: accountType,
                company_name: companyName
            });
            
            const { access_token } = response.data;
            setCookie("token", access_token, 7);
            await fetchUser(access_token);
            router.push("/dashboard");
        } catch (error) {
            console.error("Google login failed", error);
            throw error;
        }
    };

    useEffect(() => {
        const token = getCookie("token");
        if (token) {
            fetchUser(token);
        } else {
            setLoading(false);
        }
    }, []);

    const loginWithGoogle = () => {
        // Store account type and company name in session storage to use after OAuth redirect
        // This will be handled by a separate function that opens a modal first
        // For now, keep the original behavior
        window.location.href = `${API_URL}/auth/google/login`;
    };

    const login = async (email: string, password: string, captchaToken?: string | null) => {
        try {
            let response;

            if (captchaToken) {
                // Use JSON endpoint for CAPTCHA support
                response = await axios.post(`${API_URL}/auth/login/json`, {
                    email,
                    password,
                    captcha_token: captchaToken
                });
            } else {
                // Legacy Form Data flow (can be used if no captcha required)
                // Or fallback if preferred. 
                // We'll stick to legacy for compatibility if NO token is passed (though typically we might want to consolidate)
                const params = new URLSearchParams();
                params.append('username', email);
                params.append('password', password);

                response = await axios.post(`${API_URL}/auth/login`, params, {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    }
                });
            }

            const { access_token } = response.data;
            setCookie("token", access_token, 7);
            await fetchUser(access_token);
            router.push("/dashboard");
        } catch (error) {
            console.error("Login failed", error);
            throw error;
        }
    };

    const register = async (email: string, password: string, name: string, companyName?: string) => {
        try {
            console.log("Sending registration data to backend:", { email, password, name, company_name: companyName });
            await axios.post(`${API_URL}/auth/register`, {
                email,
                password,
                name,
                company_name: companyName
            });
            console.log("Registration request sent successfully");
            // Auto login after register
            await login(email, password);
        } catch (error) {
            console.error("Registration failed", error);
            throw error;
        }
    };
    
    const registerWithInvitation = async (email: string, password: string, name: string, invitationToken: string) => {
        try {
            console.log("Sending registration data with invitation to backend:", { email, password, name, invitationToken });
            const response = await axios.post(`${API_URL}/auth/register-with-invitation`, {
                email,
                password,
                name,
                invitation_token: invitationToken
            });
            console.log("Registration with invitation request sent successfully");
            
            const { access_token } = response.data;
            setCookie("token", access_token, 7);
            await fetchUser(access_token);
            router.push("/dashboard");
        } catch (error) {
            console.error("Registration with invitation failed", error);
            throw error;
        }
    };

    const logout = () => {
        setUser(null);
        deleteCookie("token");
        if (typeof window !== "undefined") {
            window.location.href = "/login";
        }
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, register, registerWithInvitation, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
